import React from 'react';
import { createPortal } from 'react-dom';
import { AcceptBlock, LangBlock } from '../../global';
import { WebStorage } from '../../../storage';
import { AdminButton } from './admin.buttons';

type Props = {
    userId: number;
    creatorBanned?: boolean;
    recommendedDeveloper?: boolean;
    onDone: () => void;
};

type MenuPosition = {
    top: number;
    left: number;
    openUp: boolean;
};

const MENU_APPROX_HEIGHT = 360;

export const AdminUserActions = ({ userId, creatorBanned, recommendedDeveloper, onDone }: Props) => {
    const [, , LangString] = LangBlock();
    const [open, setOpen] = React.useState(false);
    const [menuPos, setMenuPos] = React.useState<MenuPosition | null>(null);
    const rootRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    const updateMenuPosition = React.useCallback(() => {
        if (!triggerRef.current) {
            return null;
        }

        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < MENU_APPROX_HEIGHT;

        const nextPos = {
            top: openUp ? rect.top - 6 : rect.bottom + 6,
            left: rect.right,
            openUp,
        };

        setMenuPos(nextPos);

        return nextPos;
    }, []);

    React.useLayoutEffect(() => {
        if (!open) {
            return;
        }

        updateMenuPosition();

        const onClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }

            setOpen(false);
        };

        const onViewportChange = () => {
            updateMenuPosition();
        };

        document.addEventListener('mousedown', onClickOutside);
        window.addEventListener('resize', onViewportChange);
        window.addEventListener('scroll', onViewportChange, true);

        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('scroll', onViewportChange, true);
        };
    }, [open, updateMenuPosition]);

    React.useLayoutEffect(() => {
        if (!open) {
            setMenuPos(null);
        }
    }, [open]);

    const runBalanceAction = async (type: number) => {
        const sumStr = prompt(LangString('AdminEnterValue'), '0');

        if (!sumStr) {
            return;
        }

        const sum = parseFloat(sumStr);

        if (typeof sum !== 'number' || isNaN(sum) || sum < 0 || (sum === 0 && type !== 3)) {
            return;
        }

        await WebStorage.ApiRequest('/api/admin/user', {
            type,
            target: userId,
            amount: sum,
        });

        onDone();
    };

    const handleAction = async (action: string) => {
        setOpen(false);

        if (action === 'test') {
            if (!(await AcceptBlock())) {
                return;
            }

            await WebStorage.ApiRequest('/api/admin/userTest', { target: userId });
            onDone();
            return;
        }

        if (action === 'excludeFromStats') {
            if (!(await AcceptBlock())) {
                return;
            }

            await WebStorage.ApiRequest('/api/admin/userExcludeFromStats', { target: userId });
            onDone();
            return;
        }

        if (action === 'delete') {
            for (let i = 1; i <= 3; i++) {
                if (!(await AcceptBlock(LangString('AdminDeleteConfirm', i)))) {
                    return;
                }
            }

            await WebStorage.ApiRequest('/api/admin/removeAccount', { target: userId });
            onDone();
            return;
        }

        if (action === 'add') {
            await runBalanceAction(1);
        }

        if (action === 'remove') {
            await runBalanceAction(2);
        }

        if (action === 'set') {
            await runBalanceAction(3);
        }

        if (action === 'creatorBan') {
            const ban = !creatorBanned;
            const confirmText = ban
                ? LangString('AdminCreatorBanConfirm')
                : LangString('AdminCreatorUnbanConfirm');

            if (!(await AcceptBlock(confirmText))) {
                return;
            }

            await WebStorage.ApiRequestPost('/api/extensions/catalog/creatorBan', {
                user: String(userId),
                banned: ban ? '1' : '0',
            });

            onDone();
        }

        if (action === 'recommendedDeveloper') {
            const enable = !recommendedDeveloper;
            const confirmText = enable
                ? LangString('AdminRecommendedConfirm')
                : LangString('AdminUnrecommendConfirm');

            if (!(await AcceptBlock(confirmText))) {
                return;
            }

            await WebStorage.ApiRequest('/api/admin/userRecommendedDeveloper', { target: userId });
            onDone();
        }
    };

    const menu = open && menuPos ? (
        <div
            ref={menuRef}
            className={`admin-dropdown__menu--portal${menuPos.openUp ? ' admin-dropdown__menu--up' : ''}`}
            style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
        >
            <button type="button" onClick={() => handleAction('add')}>
                {LangString('AdminAddBalance')}
            </button>
            <button type="button" onClick={() => handleAction('remove')}>
                {LangString('AdminRemoveBalance')}
            </button>
            <button type="button" onClick={() => handleAction('set')}>
                {LangString('AdminSetBalance')}
            </button>
            <button type="button" onClick={() => handleAction('test')}>
                {LangString('AdminSwitchTest')}
            </button>
            <button type="button" onClick={() => handleAction('excludeFromStats')}>
                {LangString('AdminSwitchExcludeFromStats')}
            </button>
            <button type="button" onClick={() => handleAction('creatorBan')}>
                {creatorBanned ? LangString('AdminRemoveCreatorBan') : LangString('AdminCreatorBan')}
            </button>
            <button type="button" onClick={() => handleAction('recommendedDeveloper')}>
                {recommendedDeveloper
                    ? LangString('AdminRemoveRecommended')
                    : LangString('AdminMarkRecommended')}
            </button>
            <button
                type="button"
                className="admin-dropdown__danger"
                onClick={() => handleAction('delete')}
            >
                {LangString('AdminDeleteAccount')}
            </button>
        </div>
    ) : null;

    return (
        <div className="admin-dropdown" ref={rootRef}>
            <AdminButton
                ref={triggerRef}
                variant="ghost"
                size="sm"
                className="admin-dropdown__trigger"
                onClick={() => {
                    if (open) {
                        setOpen(false);
                        return;
                    }

                    setOpen(true);
                }}
                aria-label={LangString('AdminUserActions')}
                aria-expanded={open}
            >
                ⋮
            </AdminButton>
            {menu ? createPortal(menu, document.body) : null}
        </div>
    );
};

export const AdminAvatar = ({ src, alt }: { src?: string; alt?: string }) => (
    <div className="admin-avatar">
        {src ? <img src={src} alt={alt || ''} /> : <span>?</span>}
    </div>
);

export const AdminExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
    <a className="admin-link" href={href} target="_blank" rel="noreferrer">
        {children}
    </a>
);
